/**
 * MemberRow Component
 *
 * Displays a single household member inside the Household settings tab.
 * Owners can edit split_start_date via a calendar popover and remove
 * non-self members with inline confirmation.
 */

import { Icon, IconKey } from '@/shared/components/icon';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Calendar } from '@/shared/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { toast } from '@/shared/hooks/use-toast';
import { useDeleteUser, useUpdateUser } from '@/shared/hooks/use-user';
import type { UserRead } from '@/shared/types';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';

interface MemberRowProps {
  member: UserRead;
  isOwner: boolean;
  isSelf: boolean;
}

export function MemberRow({ member, isOwner, isSelf }: MemberRowProps) {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const initials = member.name?.charAt(0).toUpperCase() ?? '?';
  const splitDate = member.split_start_date ? parseISO(member.split_start_date) : null;
  const canEditDate = isOwner && !isSelf;
  const canRemove = isOwner && !isSelf;

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    const iso = format(date, 'yyyy-MM-dd');
    updateUser.mutate(
      { id: member.id, data: { split_start_date: iso } },
      {
        onSuccess: () => setCalendarOpen(false),
        onError: () => {
          toast({ title: 'Failed to update split date', variant: 'destructive' });
        },
      }
    );
  };

  const handleRemove = () => {
    deleteUser.mutate(member.id, {
      onSuccess: () => setConfirmingRemove(false),
      onError: () => {
        toast({ title: 'Failed to remove member', variant: 'destructive' });
        setConfirmingRemove(false);
      },
    });
  };

  // Inline remove confirmation replaces the row content
  if (confirmingRemove) {
    return (
      <div className="flex items-center gap-3 py-3 px-1">
        <Icon icon={IconKey.AlertCircle} size={18} className="text-destructive shrink-0" />
        <span className="text-sm flex-1">
          Remove <strong>{member.name}</strong>? This cannot be undone.
        </span>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmingRemove(false)}
            disabled={deleteUser.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleRemove}
            disabled={deleteUser.isPending}
          >
            {deleteUser.isPending && (
              <Icon icon={IconKey.Loading} size={14} className="animate-spin" />
            )}
            Remove
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-3">
      {/* Avatar */}
      <Avatar className="h-9 w-9 shrink-0">
        {member.avatar_url && (
          <AvatarImage src={member.avatar_url} alt={member.name} />
        )}
        <AvatarFallback
          style={member.avatar_color ? { backgroundColor: member.avatar_color } : undefined}
          className="text-white text-sm font-medium"
        >
          {initials}
        </AvatarFallback>
      </Avatar>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{member.name}</span>
          {isSelf && isOwner && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">Owner</Badge>
          )}
          {isSelf && !isOwner && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">You</Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">{member.email}</div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="text-xs text-muted-foreground">Splitting since:</span>
          {canEditDate ? (
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs font-normal"
                  disabled={updateUser.isPending}
                >
                  {updateUser.isPending ? (
                    <Icon icon={IconKey.Loading} size={12} className="animate-spin" />
                  ) : (
                    <Icon icon={IconKey.Calendar} size={12} />
                  )}
                  {splitDate ? format(splitDate, 'MMM d, yyyy') : 'Set date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={splitDate ?? undefined}
                  onSelect={handleDateSelect}
                  defaultMonth={splitDate ?? undefined}
                />
              </PopoverContent>
            </Popover>
          ) : (
            <span className="text-xs text-muted-foreground">
              {splitDate ? format(splitDate, 'MMM d, yyyy') : 'Not set'}
            </span>
          )}
        </div>
      </div>

      {/* Remove button */}
      {canRemove && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
          onClick={() => setConfirmingRemove(true)}
        >
          <Icon icon={IconKey.Trash} size={16} />
        </Button>
      )}
    </div>
  );
}
